// Employee Attendance — monthly summary table.
//
// Column defs modeled on CycleCountTable.tsx (DataTable wrapping) and
// EmployeeTable.tsx's action-column convention (single "Mark Attendance"
// button here since there's only one row action). Presentation only: data,
// search and pagination are all owned by attendanceList.tsx and passed
// straight through, same coexistence contract as the other *Table.tsx files.
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { CalendarDays } from "lucide-react";
import { DataTable } from "../../shared/data-table";
import { Button } from "ui/button";
import { cn } from "lib/utils";

// Decimal hours (e.g. 18.13) -> "hh:mm" (e.g. "18:08") — decimal hours is a
// fraction of 60 minutes, not minutes itself, so the digits after the
// decimal point don't match minutes directly.
const formatHoursAsHhMm = (decimalHours: number | null | undefined): string => {
  if (decimalHours == null || Number.isNaN(decimalHours)) {
    return "-";
  }
  const totalMinutes = Math.round(decimalHours * 60);
  const sign = totalMinutes < 0 ? "-" : "";
  const absMinutes = Math.abs(totalMinutes);
  const hh = Math.floor(absMinutes / 60);
  const mm = absMinutes % 60;
  return `${sign}${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

export interface AttendanceTableProps {
  data: any[];
  loading: boolean;
  onMarkAttendance: (record: any) => void;
  searchValue?: string;
  onSearch: (query: string) => void;
  searchPlaceholder?: string;
  title?: React.ReactNode;
  toolbar?: React.ReactNode;
  headerActions?: React.ReactNode;
  pagination: { page: number; total: number; onChange: (page: number) => void };
  getRowClassName?: (row: any) => string | undefined;
}

export function AttendanceTable({
  data,
  loading,
  onMarkAttendance,
  searchValue,
  onSearch,
  searchPlaceholder,
  title,
  toolbar,
  headerActions,
  pagination,
  getRowClassName,
}: AttendanceTableProps) {
  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        id: "employeeCode",
        header: "ID",
        cell: ({ row }) => <span className="whitespace-nowrap font-medium">{row.original?.employeeCode || "-"}</span>,
      },
      {
        id: "employeeName",
        header: "Employee Name",
        cell: ({ row }) => <span className="font-medium">{row.original?.employeeName}</span>,
      },
      {
        id: "workType",
        header: "Work Type",
        cell: ({ row }) => row.original?.workType || "-",
      },
      {
        id: "allowedLeaveDays",
        header: "Allowed Leave Days",
        cell: ({ row }) => row.original?.allowedLeaveDays ?? "-",
      },
      {
        id: "actualWorkingHours",
        header: "Actual Working Hrs",
        cell: ({ row }) => formatHoursAsHhMm(row.original?.actualWorkingHours),
      },
      {
        id: "expectedWorkingHrs",
        header: "Expected Working Hrs",
        cell: ({ row }) => formatHoursAsHhMm(row.original?.expectedWorkingHrs),
      },
      {
        id: "actualLeaveTaken",
        header: "Actual Leave Taken",
        cell: ({ row }) => row.original?.actualLeaveTaken ?? 0,
      },
      {
        id: "lop",
        header: "LOP",
        cell: ({ row }) => {
          const lop = row.original?.lop ?? 0;
          return (
            <span className={cn("font-medium", lop > 0 && "text-destructive")}>{lop}</span>
          );
        },
      },
      {
        id: "action",
        header: "Action",
        cell: ({ row }) => {
          const record = row.original;
          return (
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={(e) => {
                e.stopPropagation();
                onMarkAttendance(record);
              }}
            >
              <CalendarDays className="h-4 w-4" />
              Mark Attendance
            </Button>
          );
        },
        meta: { sticky: "right" },
      },
    ],
    [onMarkAttendance]
  );

  return (
    <DataTable
      columns={columns}
      data={data || []}
      loading={loading}
      searchable
      searchValue={searchValue}
      onSearch={onSearch}
      searchPlaceholder={searchPlaceholder}
      title={title}
      toolbar={toolbar}
      headerActions={headerActions}
      pagination={pagination}
      getRowClassName={getRowClassName}
    />
  );
}

export default AttendanceTable;
